import type { TypographyProps } from '@mui/material'
import { alpha } from '@mui/material'
import { createTheme as muiCreateTheme } from '@mui/material/styles'

import { components } from './components'
import { createPalette } from './palette'
import { shadows } from './shadows'
import type { AppTheme, ThemeColors } from './types'

function getSecondaryButtonStyles(colors: ThemeColors) {
  return {
    '&:disabled': {
      color: colors.disabled,
    },
    '&:hover, &.Mui-focusVisible, &:hover, &.Mui-active': {
      color: colors.active[1],
    },
    background: 'transparent',
    borderRadius: 6,
    color: colors.main[3],
    padding: 8,
    transition: '200ms',
  }
}

export const createTheme = (colors: ThemeColors): AppTheme =>
  muiCreateTheme({
    breakpoints: {
      values: {
        lg: 1280,
        md: 900,
        sm: 600,
        xl: 1536,
        xs: 0,
      },
    },
    components: {
      ...components(colors),
      MuiAccordion: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            '&&.Mui-expanded:before': {
              opacity: 1,
            },
            background: colors.background.default,
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            padding: 0,
          },
        },
      },
      MuiAccordionSummary: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          content: {
            '&.Mui-expanded': {
              margin: '12px 0',
            },
          },
          expandIconWrapper: {
            ...getSecondaryButtonStyles(colors),
            border: 'none',
          },
          root: {
            '&&&': {
              paddingBottom: 0,
              paddingTop: 0,
            },
            '&.Mui-expanded': {
              minHeight: 'inherit',
            },
            '&:hover': {
              backgroundColor: colors.active['5'],
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            // Only the floating usages — the notification toast stack and any
            // Snackbar — are lifted off the page. The same class backs in-card
            // banners, which must sit flat and fill their column.
            '.MuiSnackbar-root &, &[data-test="notification"]': {
              boxShadow: shadows(colors.theme)?.[5],
            },
            border: `1px solid ${colors.divider}`,
            borderRadius: 4,
            boxShadow: 'none',
            fontSize: '1.6rem',
            width: '100%',
          },
          standard: {
            '&.MuiAlert-colorError': {
              '& .MuiAlert-icon': {
                color: colors.alert.icon.error,
              },
              backgroundColor: colors.alert.paper.error,
              color: colors.main[1],
            },
            '&.MuiAlert-colorInfo': {
              '& .MuiAlert-icon': {
                color: colors.alert.icon.info,
              },
              backgroundColor: colors.alert.paper.info,
              color: colors.main[1],
            },
            '&.MuiAlert-colorSuccess': {
              '& .MuiAlert-icon': {
                color: colors.alert.icon.success,
              },
              backgroundColor: colors.alert.paper.success,
              color: colors.main[1],
            },
            '&.MuiAlert-colorWarning': {
              '& .MuiAlert-icon': {
                color: colors.alert.icon.warning,
              },
              backgroundColor: colors.alert.paper.warning,
              color: colors.main[1],
            },
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '& .MuiTouchRipple-child': {
              backgroundColor: alpha(colors.active[1], 1),
            },
            '& .MuiTouchRipple-childLeaving': {
              backgroundColor: alpha(colors.active[1], 1),
            },
            '& .MuiTouchRipple-ripple': {
              borderRadius: '100%',
            },
            '& .MuiTouchRipple-rippleVisible': {
              backgroundColor: alpha(colors.active[1], 1),
            },
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: colors.background.paper,
            border: `1px solid ${colors.divider}`,
            borderRadius: 8,
            // xs shadow at rest — border-defined cards with a whisper of depth
            boxShadow: shadows(colors.theme)?.[1],
            overflow: 'visible',
          },
        },
      },
      MuiCardActions: {
        styleOverrides: {
          root: {
            '.MuiCardContent-root + &': {
              paddingTop: 0,
            },
            padding: '3.2rem 3.2rem 3.2rem',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            '.MuiCardHeader-root + &': {
              paddingTop: 0,
            },
            padding: '2.4rem 3.2rem',
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          action: {
            position: 'relative',
            top: 4,
          },
          root: {
            padding: '2.4rem 3.2rem',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          avatar: {
            fontSize: '1.2rem',
            height: 32,
            left: -5,
            position: 'relative',
            width: 32,
          },
          label: {
            alignItems: 'center',
            display: 'flex',
          },
          root: {
            borderRadius: 6,
            fontSize: '1.3rem',
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--background-default': colors.background.default,
            '--background-paper': colors.background.paper,
            '--default-font': "'Inter', sans-serif",
          },

          '@keyframes hoverControl': {
            from: {
              transform: 'scale(1.4)',
            },
            to: {
              transform: 'scale(1.6)',
            },
          },

          '@keyframes hoverControlStart': {
            from: {
              transform: 'scale(1)',
            },
            to: {
              transform: 'scale(1.4)',
            },
          },

          '@keyframes hoverSwitch': {
            from: {
              boxShadow: `0 0 0 3px ${colors.active[5]}`,
            },
            to: {
              boxShadow: `0 0 0 5px ${colors.active[5]}`,
            },
          },
          '@keyframes hoverSwitchStart': {
            from: {
              boxShadow: `0 0 0 0 ${colors.active[5]}`,
            },
            to: {
              boxShadow: `0 0 0 3px ${colors.active[5]}`,
            },
          },

          '@supports (font-variation-settings: normal)': {
            ':root': {
              '--default-font': "'Inter var', sans-serif",
            },
          },

          // For some reason @import clause must be put on top
          // eslint-disable-next-line sort-keys
          '::selection': {
            background: alpha(colors.active[1], 0.2),
          },
          a: {
            color: colors.active[1],
            textDecoration: 'none',
          },
          form: {
            margin: 0,
          },
          html: {
            fontSize: '50.782%',
            overflowY: 'scroll',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            borderRadius: 12,
            overflowY: undefined,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            borderTopColor: alpha(colors.divider, 0.5),
            borderTopStyle: 'solid',
            borderTopWidth: 1,
            padding: '16px 24px',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            margin: '0px 24px',
            overflowX: undefined,
            overflowY: undefined,
            padding: '24px 0px',
          },
        },
      },
      MuiDialogContentText: {
        styleOverrides: {
          root: {
            '&:last-child': {
              marginBottom: 0,
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: colors.background.default,
            borderBottomRightRadius: 32,
            borderTopRightRadius: 32,
            padding: 24,
            width: 260,
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          variant: 'filled',
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            lineHeight: 1.2,
            marginLeft: 4,
          },
          root: {
            marginLeft: -14,
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: {
            fontSize: '1.3rem',
            verticalAlign: 'top',
          },
          filled: {
            '&&:not(.Mui-error)': {
              color: colors.active[1],
            },
          },
          // No `root` focus colour here: it serialises after MuiInputLabel and
          // silently beat the teal focus colour that rule (and `filled` above)
          // asks for. Focused labels are owned by MuiInputLabel.
        },
      },
      MuiList: {
        defaultProps: {
          disablePadding: true,
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            // Selection is a teal wash with a teal label — `main[1]` is the
            // *text* colour, so painting it as the background rendered the row
            // as text-on-itself.
            '&.Mui-selected': {
              '&:hover': {
                backgroundColor: alpha(colors.active[1], 0.18),
              },
              backgroundColor: alpha(colors.active[1], 0.12),
              color: colors.active[1],
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&&:active': {
              backgroundColor: alpha(colors.active[1], 0.16),
            },
            // Keyboard focus is the selected wash plus an inset ring, so it
            // never reads as a plain hover.
            '&.Mui-focusVisible': {
              backgroundColor: alpha(colors.active[1], 0.12),
              outline: `2px solid ${alpha(colors.active[1], 0.5)}`,
              outlineOffset: -2,
            },
            '&.Mui-selected': {
              '&:hover': {
                backgroundColor: alpha(colors.active[1], 0.18),
              },
              backgroundColor: alpha(colors.active[1], 0.12),
              color: colors.active[1],
            },
            '&:hover': {
              backgroundColor: alpha(colors.active[1], 0.08),
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            // Level 8 is the dropdown step of the ramp, and it carries ~4x the
            // alpha on dark so the menu still separates from navy paper.
            boxShadow: shadows(colors.theme)?.[8],
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            // Keyboard focus repeats the hover wash and adds an inset ring, so
            // arrowing through the menu never looks like a mouse hover.
            '&.Mui-focusVisible': {
              backgroundColor: colors.active[5],
              color: colors.active[1],
              outline: `2px solid ${alpha(colors.active[1], 0.5)}`,
              outlineOffset: -2,
            },
            '&.Mui-selected, &.Mui-selected:focus, &.Mui-selected:hover': {
              backgroundColor: colors.active[5],
              color: colors.active[1],
            },
            '&:hover': {
              backgroundColor: colors.active[5],
            },
            '@media(min-width: 600px)': {
              minHeight: 48,
            },
            cursor: 'pointer',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            background: colors.background.paper,
            borderRadius: 8,
            boxShadow: shadows(colors.theme)?.[8],
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          MenuProps: {
            anchorOrigin: {
              horizontal: 'left',
              vertical: 'bottom',
            },
            transformOrigin: {
              horizontal: 'left',
              vertical: 'top',
            },
          },
          variant: 'outlined',
        },
        styleOverrides: {
          select: {
            '&.Mui-disabled': {
              backgroundColor: colors.background.default,
            },
          },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: {
          action: {
            '& .Mui-IconButton': {
              '& svg': {
                color: colors.main[1],
              },
            },
            display: 'block',
            paddingBottom: '1.2rem',
            paddingLeft: 0,
            paddingRight: '5.6rem',
          },
          message: {
            fontSize: '1.6rem',
          },
          root: {
            backgroundColor: colors.background.paper,
            boxShadow: shadows(colors.theme)?.[5],
            color: colors.main[1],
            display: 'block',
            // ~480px at the 50.782% root — the toast column width
            maxWidth: '59rem',
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            fontSize: '2.4rem',
            height: '1em',
            width: '1em',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiTooltip: {
        defaultProps: {
          placement: 'right-end',
        },
        styleOverrides: {
          arrow: {
            color: colors.alert.icon.info,
          },
          tooltip: {
            backgroundColor: colors.alert.icon.info,
            color: colors.active[4],
            fontSize: '1.2rem',
            padding: '1.6rem',
          },
        },
      },
      MuiTypography: {
        defaultProps: {
          component: 'div',
        } as TypographyProps,
      },
    },
    palette: createPalette(colors),
    shadows: shadows(colors.theme),
    spacing: (value: number = 1) => `${(value * 8) / 10}rem`,
    typography: {
      allVariants: {
        fontFamily: 'var(--default-font)',
        letterSpacing: '0.01rem',
      },
      body1: {
        color: colors.main[1],
        fontSize: '1.6rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '1.4rem',
        lineHeight: 1.6,
      },
      button: {
        fontSize: '1.4rem',
        fontWeight: 500,
      },
      caption: {
        // Captions are secondary text. Without this, every
        // `...theme.typography.caption` spread inherited body colour and
        // `theme.typography.caption.color` read as undefined.
        color: colors.main[3],
        fontSize: '1.2rem',
        letterSpacing: 0,
        lineHeight: 1.5,
      },
      fontFamily: 'var(--default-font)',
      h1: {
        fontSize: '4rem',
        fontWeight: 700,
        letterSpacing: '-0.02rem',
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '3.5rem',
      },
      h3: {
        fontSize: '2.9rem',
      },
      h4: {
        fontSize: '2.5rem',
        fontWeight: 600,
        letterSpacing: '-0.01rem',
      },
      h5: {
        fontSize: '2.1rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '2rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      subtitle1: {
        fontSize: '1.6rem',
        fontWeight: 500,
      },
      subtitle2: {
        fontSize: '1.4rem',
        fontWeight: 500,
      },
    },
  }) as unknown as AppTheme
